import * as React from 'react';
import { Fragment } from 'react';
import { action, observable, computed } from 'mobx';
import { observer } from 'mobx-react';
import { inject } from '../../utils';
import { withTheme } from 'styled-components';
import { camelCase } from 'change-case';

import { KEYBOARD_KEYS, KEYBOARD_STATE_KEY } from '../../constants';

import formatter from '../../utils/formatter';
import { generateGuid } from '../../utils/generateGuid';
import { KeyboardEvents } from '../../utils/accessibility/KeyboardEvents';
import { ThemeableComponent } from '../../utils/theme/ThemeableComponent';

import ClickLayer from '../ClickLayer';
import Icon from '../Icon';
import Translate from '../Translate';

import {
    ClearButton,
    Dropdown,
    Input,
    InputWrapper,
    LabelWrapper,
    Label,
    ListItem,
    OrderedList,
    Wrapper,
} from './StyledComponents';

import {
    AutocompleteItem,
    AutocompleteItemFormatted,
    AutocompleteProps,
    ThemeKeys,
} from './Models';

@inject('accessibilityStore', 'dataStore')
@observer
class Autocomplete extends ThemeableComponent<
    AutocompleteProps,
    {},
    ThemeKeys
> {
    @observable.ref expanded = false;
    @observable.ref valuePreview = '';
    @observable.ref rawValueFilter = '';
    @observable.ref rawSelectedItem: AutocompleteItemFormatted;
    @observable.ref selectedItemOffset = 0;
    @observable.ref selectedItemInstance: HTMLLIElement | undefined;
    @observable.ref orderedListInstance: HTMLOListElement | undefined;
    @observable.ref inputInstance: HTMLInputElement | undefined;

    guid: string;
    inputKeyboardEvents: KeyboardEvents<HTMLInputElement>;
    clearButtonKeyboardEvents: KeyboardEvents<HTMLLIElement>;

    @computed
    get defaultIndex(): number {
        const { defaultIndex = 'first' } = this.props;
        return defaultIndex === 'first' ? 0 : this.filteredItems.length - 1;
    }

    @computed
    get valueFilter() {
        return this.rawValueFilter;
    }

    set valueFilter(value: string) {
        this.rawValueFilter = value;
        this.valuePreview = value;
    }

    @computed
    get selectedItem(): AutocompleteItemFormatted {
        return this.rawSelectedItem;
    }

    set selectedItem(item: AutocompleteItemFormatted) {
        this.rawSelectedItem =
            item.formattedValue === undefined ? this.formatItem(item) : item;
    }

    @computed
    get selectedItemIndex(): number {
        if (this.selectedItem && this.filteredItems.length > 0) {
            return this.filteredItems.findIndex(
                item => item.value === this.selectedItem.value
            );
        }
        return -1;
    }

    @computed
    get dataMetricIdPrefix() {
        return this.props.dataMetricIdPrefix;
    }

    @computed
    get locale() {
        const { dataStore } = this.props;
        if (dataStore) {
            return dataStore.culture;
        }
    }

    @computed
    get formattedItem(): AutocompleteItemFormatted {
        return this.formatItem(this.selectedItem);
    }

    @computed
    get formattedItems(): Array<AutocompleteItemFormatted> {
        const { items } = this.props;
        return items.map(item => {
            return this.formatItem(item);
        });
    }

    @computed
    get filteredItems(): Array<AutocompleteItemFormatted> {
        return this.formattedItems
            .filter(item =>
                item.formattedValue
                    .toLowerCase()
                    .startsWith(this.valueFilter.toLowerCase())
            )
            .sort();
    }

    get inputKeyboardProps() {
        return this.inputKeyboardEvents.onKeyProps;
    }

    get clearButtonKeyboardProps() {
        return this.clearButtonKeyboardEvents.onKeyProps;
    }

    constructor(props: AutocompleteProps) {
        super(props);
        this.guid = generateGuid();
        this.selectedItem = props.selectedItem as AutocompleteItemFormatted;
        this.initializeInputKeyboardEvents();
        this.initializeClearButtonKeyboardEvents();
    }

    componentDidUpdate(prevProps: AutocompleteProps) {
        if (prevProps.selectedItem.value !== this.props.selectedItem.value) {
            this.selectedItem = this.props
                .selectedItem as AutocompleteItemFormatted;
        }
    }

    initializeInputKeyboardEvents() {
        this.inputKeyboardEvents = new KeyboardEvents({
            name: 'AutocompleteInput',
            debug: false,
            defaults: {
                navigation: {
                    down: {
                        stopPropagation: true,
                        preventDefault: true,
                    },
                },
            },
        });
        this.inputKeyboardEvents.bindKeyEvent(
            KEYBOARD_KEYS.DOWN_ARROW,
            'down',
            e => {
                if (!this.expanded) {
                    this.toggleExpanded(true);
                } else {
                    this.highlightNextItem(this.selectedItemIndex + 1);
                }
            }
        );
        this.inputKeyboardEvents.bindKeyEvent(
            KEYBOARD_KEYS.UP_ARROW,
            'down',
            e => {
                if (!this.expanded) {
                    this.toggleExpanded(true);
                } else {
                    this.highlightNextItem(this.selectedItemIndex - 1);
                }
            }
        );
        this.inputKeyboardEvents.bindKeyEvent(KEYBOARD_KEYS.ENTER, 'up', e => {
            this.setSelectedItem(this.selectedItem);
        });
        this.inputKeyboardEvents.bindKeyEvent(KEYBOARD_KEYS.TAB, 'down', e => {
            this.selectedItem = this.props
                .selectedItem as AutocompleteItemFormatted;
            this.toggleExpanded(false);
        });
        this.inputKeyboardEvents.bindKeyEvent(KEYBOARD_KEYS.ESCAPE, 'up', e => {
            this.clearAutocomplete();
        });
    }

    initializeClearButtonKeyboardEvents() {
        this.clearButtonKeyboardEvents = new KeyboardEvents({
            name: 'AutocompleteListItem',
            debug: false,
        });
        this.clearButtonKeyboardEvents.bindKeyEvent(
            KEYBOARD_KEYS.ESCAPE,
            'up',
            e => {
                this.clearAutocomplete();
                if (this.inputInstance) {
                    this.inputInstance.focus();
                }
            }
        );
        this.clearButtonKeyboardEvents.bindStateEvent(
            KEYBOARD_STATE_KEY.ACCEPT,
            'down',
            e => {
                e.preventDefault();
                e.stopPropagation();
            }
        );
        this.clearButtonKeyboardEvents.bindStateEvent(
            KEYBOARD_STATE_KEY.ACCEPT,
            'up',
            e => {
                e.preventDefault();
                e.stopPropagation();
                this.clearAutocomplete();
                if (this.inputInstance) {
                    this.inputInstance.focus();
                }
            }
        );
    }

    formatItem(item: AutocompleteItem): AutocompleteItemFormatted {
        const { dataStore, valueFormat } = this.props;
        const formattedItem = {
            ...item,
            formattedValue: item.value,
        };
        if (valueFormat) {
            formattedItem.formattedValue = formatter({
                dataStore,
                format: { ...valueFormat },
                type: valueFormat.type || 'date',
                options: { ...valueFormat.options },
                value: item.value,
                locale: this.locale,
            });
        }
        return formattedItem;
    }

    @action
    highlightNextItem(index: number): void {
        const listLength = this.filteredItems.length;
        if (listLength <= index) {
            index = 0;
        } else if (index < 0) {
            index = listLength - 1;
        }
        const nextItem = this.filteredItems[index];
        this.setHighlightedItem(
            nextItem,
            index < this.selectedItemIndex ? 'up' : 'down'
        );
    }

    @action
    toggleExpanded(explicit?: boolean) {
        const newState = explicit !== undefined ? explicit : !this.expanded;
        if (newState !== this.expanded) {
            this.expanded = newState;
        }
    }

    @action
    resetSelectedItem(closeExpand?: boolean) {
        const { setSelectedItem } = this.props;
        if (closeExpand) {
            this.toggleExpanded(false);
        }
        this.selectedItem = this.filteredItems[this.defaultIndex];
        if (setSelectedItem) {
            setSelectedItem(this.selectedItem);
        }
    }

    @action
    setSelectedValue(value: string) {
        this.valueFilter = value;
    }

    @action
    clearAutocomplete() {
        this.setSelectedValue('');
        this.resetSelectedItem(true);
    }

    @action
    setHighlightedItem(
        item: AutocompleteItemFormatted,
        scrollDirection?: 'down' | 'up'
    ) {
        if (item) {
            this.selectedItem = item;
            this.updateOrderedListScrollTop(scrollDirection);
            this.valuePreview = this.selectedItem.formattedValue;
        }
    }

    @action
    setSelectedItem(item: AutocompleteItemFormatted) {
        const { setSelectedItem } = this.props;
        this.toggleExpanded(false);
        this.valueFilter = item.formattedValue;
        if (setSelectedItem) {
            setSelectedItem(item);
        }
    }

    @action
    getInputReference = (instance: HTMLInputElement | null) => {
        if (instance) {
            this.inputInstance = instance;
        }
    };

    @action
    getSelectedItemReference = (instance: HTMLLIElement | null) => {
        if (instance) {
            this.selectedItemInstance = instance;
            this.selectedItemOffset = instance.offsetTop;
        }
    };

    @action
    getOrderedListReference = (instance: HTMLOListElement | null) => {
        if (instance) {
            this.orderedListInstance = instance;
            this.updateOrderedListScrollTop();
        }
    };

    @action
    updateOrderedListScrollTop(scrollDirection?: 'down' | 'up') {
        if (this.orderedListInstance) {
            this.orderedListInstance.scrollTop =
                this.selectedItemOffset + (scrollDirection === 'up' ? -50 : 0);
        }
    }

    getMetricId(suffix: string) {
        return `${this.dataMetricIdPrefix}${suffix}`;
    }

    clearClickEventHandler = (e: React.MouseEvent<HTMLButtonElement>) => {
        this.clearAutocomplete();
    };

    inputChangeEventHandler = (e: React.ChangeEvent<HTMLInputElement>) => {
        this.toggleExpanded(true);
        this.setSelectedValue(e.target.value);
        if (this.filteredItems.length > 0) {
            this.resetSelectedItem();
            this.orderedListInstance.scrollTop = 0;
        }
    };

    render() {
        const { label, accessibilityStore } = this.props;
        return (
            <Wrapper
                id={`autocomplete-combobox-${this.guid}`}
                role={'combobox'}
                aria-expanded={this.expanded}
                aria-haspopup={'listbox'}
                aria-owns={`autocomplete-listbox-${this.guid}`}
                {...this.getTheme('wrapper')}
            >
                <LabelWrapper {...this.getTheme('labelWrapper')}>
                    <Label
                        id={`autocomplete-label-${this.guid}`}
                        {...this.getTheme('label')}
                    >
                        <Translate>{label}</Translate>
                    </Label>
                    <InputWrapper
                        data-selected={this.expanded}
                        {...this.getTheme('inputWrapper')}
                    >
                        <Input
                            id={`autocomplete-input-${this.guid}`}
                            ref={this.getInputReference}
                            placeholder={this.formattedItem.formattedValue}
                            value={this.valuePreview}
                            onChange={this.inputChangeEventHandler}
                            onClick={() => this.toggleExpanded(true)}
                            onFocus={() => this.toggleExpanded(true)}
                            data-metric-id={this.getMetricId(
                                `Input-${camelCase(label)}`
                            )}
                            aria-autocomplete={'both'}
                            aria-controls={`autocomplete-listbox-${this.guid}`}
                            aria-labelledby={`autocomplete-label-${this.guid}`}
                            aria-activedescendant={`autocomplete-listitem-${this.guid}-${this.selectedItemIndex}`}
                            {...this.inputKeyboardProps}
                            {...this.getTheme('input')}
                        />
                        {this.valuePreview.length > 0 && (
                            <ClearButton
                                aria-label={accessibilityStore.buttonAccessibilityValue(
                                    'DateSelectorFilterClearButton'
                                )}
                                aria-describedby={`autocomplete-label-${this.guid}`}
                                onClick={this.clearClickEventHandler}
                                data-metric-id={this.getMetricId(`FilterClear`)}
                                {...this.clearButtonKeyboardProps}
                                {...this.getTheme('filterClearButton')}
                            >
                                <Icon
                                    fileName="CloseIcon"
                                    height="1em"
                                    width="1em"
                                    {...this.getTheme('filterClearButtonIcon')}
                                />
                            </ClearButton>
                        )}
                    </InputWrapper>
                </LabelWrapper>
                {this.expanded && (
                    <Fragment>
                        <ClickLayer
                            data-metric-id={this.getMetricId(`-clickLayer`)}
                            onClick={() => this.toggleExpanded(false)}
                        />
                        <Dropdown {...this.getTheme('dropdown')}>
                            <OrderedList
                                id={`autocomplete-listbox-${this.guid}`}
                                ref={this.getOrderedListReference}
                                role={'listbox'}
                                aria-labelledby={`autocomplete-label-${this.guid}`}
                                {...this.getTheme('dropdownList')}
                            >
                                {this.filteredItems.map((item, itemIndex) => {
                                    const isSelected =
                                        this.selectedItem.value === item.value;
                                    return (
                                        <ListItem
                                            id={`autocomplete-listitem-${this.guid}-${itemIndex}`}
                                            key={item.value}
                                            ref={
                                                isSelected
                                                    ? this
                                                          .getSelectedItemReference
                                                    : null
                                            }
                                            role={'option'}
                                            data-alternated={
                                                itemIndex % 2 ? true : false
                                            }
                                            data-selected={isSelected}
                                            data-metric-id={this.getMetricId(
                                                `-listItem-${camelCase(
                                                    item.formattedValue
                                                )}`
                                            )}
                                            onClick={e => {
                                                this.setSelectedItem(item);
                                            }}
                                            aria-selected={isSelected}
                                            {...this.getTheme(
                                                'dropdownListItem'
                                            )}
                                        >
                                            {item.formattedValue}
                                        </ListItem>
                                    );
                                })}
                            </OrderedList>
                        </Dropdown>
                    </Fragment>
                )}
            </Wrapper>
        );
    }
}

export default withTheme(Autocomplete);



// WEBPACK FOOTER //
// ./src/Components/Autocomplete/index.tsx